import {
  collection,
  query,
  orderBy,
  onSnapshot,
  updateDoc,
  doc,
  getDocs,
  type Unsubscribe
} from 'firebase/firestore';
import { db } from '../../../lib/firebase';
import type { Medication, User } from '@campusmeds/shared';
import { COLLECTIONS } from '@campusmeds/shared';

interface PatientSubmission {
  id: string;
  phoneNumber: string;
  name: string;
  dateOfBirth: string;
  medicationName: string;
  dosage: string;
  quantity: string;
  daySupply: string;
  lastPickupDate: string;
  pharmacy: string;
  prescriber: string;
  smsConsent: boolean;
  createdAt: any;
}

interface Patient {
  id: string;
  email?: string;
  name?: string;
  phoneNumber?: string;
  tier?: 'FREE' | 'PRO';
  medications?: any[];
  createdAt: Date;
  updatedAt: Date;
}

/**
 * Helper function to map old status fields to workflowStatus for backward compatibility
 */
function mapToWorkflowStatus(med: any): string {
  if (med.workflowStatus) {
    return med.workflowStatus;
  }

  // Backward compatibility: map from old fields
  if (med.verificationStatus) {
    switch (med.verificationStatus) {
      case 'PENDING':
        return 'NEEDS_CALL';
      case 'VERIFIED':
        return 'COMPLETED';
      case 'FAILED':
      case 'NOT_AVAILABLE':
        return 'PROBLEM';
      default:
        return 'NEEDS_CALL';
    }
  }

  if (med.status === 'INACTIVE') {
    return 'INACTIVE';
  }

  // Default fallback
  return 'NEEDS_CALL';
}

export class AdminService {
  static subscribeToMedications(callback: (medications: Medication[]) => void): Unsubscribe {
    const q = query(
      collection(db, COLLECTIONS.MEDICATIONS),
      orderBy('createdAt', 'desc')
    );

    return onSnapshot(q, (snapshot) => {
      console.log('🔍 Firestore medications query returned:', snapshot.docs.length, 'documents');
      if (snapshot.docs.length > 0) {
        console.log('📋 First medication raw data:', snapshot.docs[0].data());
      }

      const medications = (snapshot.docs
        .map(doc => {
          const data = doc.data();
          const workflowStatus = mapToWorkflowStatus(data);
          return {
            id: doc.id,
            ...data,
            workflowStatus, // Ensure workflowStatus is always set
            createdAt: data.createdAt?.toDate() || new Date(),
            updatedAt: data.updatedAt?.toDate() || new Date(),
            lastRefillDate: data.lastRefillDate?.toDate() || undefined,
            lastPickupDate: data.lastPickupDate ? new Date(data.lastPickupDate) : undefined,
            nextRefillDate: data.nextRefillDate?.toDate() || undefined
          };
        }) as Medication[])
        .filter(med => med.workflowStatus !== 'INACTIVE'); // Filter out inactive medications

      console.log('✅ Transformed medications:', medications.length);
      if (medications.length > 0) {
        console.log('📦 Transformed medication sample:', medications[0]);
      }

      callback(medications);
    });
  }

  static subscribeToUsers(callback: (users: User[]) => void): Unsubscribe {
    const q = query(
      collection(db, COLLECTIONS.USERS),
      orderBy('createdAt', 'desc')
    );

    return onSnapshot(q, (snapshot) => {
      const users = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
        updatedAt: doc.data().updatedAt?.toDate() || new Date()
      })) as User[];

      callback(users);
    });
  }

  static subscribeToPatients(callback: (patients: Patient[]) => void): Unsubscribe {
    const q = query(
      collection(db, 'patients'),
      orderBy('createdAt', 'desc')
    );

    return onSnapshot(q, (snapshot) => {
      const patients = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
        updatedAt: doc.data().updatedAt?.toDate() || new Date()
      })) as Patient[];

      callback(patients);
    });
  }

  static subscribeToPatientSubmissions(callback: (submissions: PatientSubmission[]) => void): Unsubscribe {
    const q = query(
      collection(db, 'patients'),
      orderBy('createdAt', 'desc')
    );

    return onSnapshot(q, (snapshot) => {
      const submissions: PatientSubmission[] = [];

      snapshot.docs.forEach(patientDoc => {
        const patientData = patientDoc.data();
        const phoneNumber = patientData.phoneNumber || patientDoc.id;

        // Extract medications from patient document
        if (patientData.medications && Array.isArray(patientData.medications)) {
          patientData.medications.forEach((medication: any) => {
            submissions.push({
              id: medication.medicationDocId || medication.id || `${patientDoc.id}-${Date.now()}`,
              phoneNumber,
              name: medication.name || 'N/A',
              dateOfBirth: medication.dateOfBirth || 'N/A',
              medicationName: medication.medicationName || 'N/A',
              dosage: medication.dosage || 'N/A',
              quantity: medication.quantity || 'N/A',
              daySupply: medication.daySupply || 'N/A',
              lastPickupDate: medication.lastPickupDate || 'Never',
              pharmacy: medication.pharmacy || 'N/A',
              prescriber: medication.prescriber || 'N/A',
              smsConsent: medication.smsConsent || false,
              createdAt: medication.createdAt || patientData.createdAt
            });
          });
        }
      });

      // Sort by creation date (most recent first)
      submissions.sort((a, b) => {
        const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt || 0);
        const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt || 0);
        return dateB.getTime() - dateA.getTime();
      });

      callback(submissions);
    });
  }

}